import {
  Attribute,
  BaseStyleId,
  Bucket,
  MapLayer,
  MapSchema,
} from "@cartographerio/atlas-map";
import { Feature } from "@cartographerio/geometry";
import React, { ReactElement, useCallback, useState } from "react";
import { Dict, Setter } from "../../hooks/useDict";
import AttributeInspector from "./AttributeInspector";
import LayerInspector from "./LayerInspector";
import RootInspector from "./RootInspector";

export interface InspectorPanelProps {
  schema: MapSchema;
  layerVisibility: Dict<boolean>;
  setLayerVisibility: Setter<boolean>;
  selectedLayer: MapLayer | undefined;
  setSelectedLayer: (layer: MapLayer | undefined) => void;
  currentAttributes: Dict<Attribute>;
  currentBuckets: Dict<Dict<Bucket[]>>;
  setCurrentAttribute: (layer: MapLayer, attr: Attribute) => void;
  baseStyleId: BaseStyleId;
  setBaseStyleId: (style: BaseStyleId) => void;
  selectedFeatures: { [source: string]: Feature[] };
  onClearFeatureSelection: () => void;
}

export default function InspectorPanel(
  props: InspectorPanelProps
): ReactElement {
  const {
    schema,
    layerVisibility,
    setLayerVisibility,
    selectedLayer,
    setSelectedLayer,
    currentAttributes,
    currentBuckets,
    setCurrentAttribute,
    baseStyleId,
    setBaseStyleId,
    selectedFeatures,
    onClearFeatureSelection,
  } = props;

  const [showAttributeInspector, setShowAttributeInspector] = useState(false);

  const currentAttribute: Attribute | null =
    selectedLayer != null ? currentAttributes[selectedLayer.layerId] : null;

  const currentLayerBuckets: Record<string, Bucket[]> | null =
    selectedLayer != null ? currentBuckets[selectedLayer.layerId] : null;

  const currentAttributeBuckets: Bucket[] | null =
    currentLayerBuckets != null && currentAttribute != null
      ? currentLayerBuckets[currentAttribute.attributeId]
      : null;

  const currentFeatures: Feature[] | null =
    selectedLayer != null ? selectedFeatures[selectedLayer.layerId] : null;

  const handleLayerClick = useCallback(
    (layer, focus, altKey) => {
      if (focus) {
        setLayerVisibility(layer.layerId, true);
        setSelectedLayer(layer);
      } else if (altKey) {
        setLayerVisibility(layer.layerId, true);

        // Toggle visibility of layers other than this one:
        const otherLayers = schema.layers.filter(
          l => l.layerId !== layer.layerId
        );

        if (otherLayers.length === 0) {
          return;
        }

        // Use the current visibilty of an arbitrary other layer
        // to work out whether to show/hide:
        const visibility = !layerVisibility[otherLayers[0].layerId];

        otherLayers.forEach(l => setLayerVisibility(l.layerId, visibility));
      } else {
        setLayerVisibility(layer.layerId, !layerVisibility[layer.layerId]);
      }
    },
    [layerVisibility, schema.layers, setLayerVisibility, setSelectedLayer]
  );

  return selectedLayer == null || currentAttribute == null ? (
    <RootInspector
      schema={schema}
      layerVisibility={layerVisibility}
      currentAttributes={currentAttributes}
      baseStyleId={baseStyleId}
      setBaseStyleId={setBaseStyleId}
      selectedFeatures={selectedFeatures}
      onLayerClick={handleLayerClick}
      onClearFeatureSelection={onClearFeatureSelection}
    />
  ) : showAttributeInspector ? (
    <AttributeInspector
      layer={selectedLayer}
      attribute={currentAttribute}
      buckets={currentAttributeBuckets ?? []}
      onBackClick={() => setShowAttributeInspector(false)}
      selectedFeatures={currentFeatures ?? []}
      onClearFeatureSelection={onClearFeatureSelection}
    />
  ) : (
    <LayerInspector
      layer={selectedLayer}
      onBackClick={() => setSelectedLayer(undefined)}
      selectedAttribute={currentAttribute}
      buckets={currentLayerBuckets ?? {}}
      selectedFeatures={currentFeatures ?? []}
      onAttributeClick={(attr, focus) => {
        setCurrentAttribute(selectedLayer, attr);
        if (focus) setShowAttributeInspector(true);
      }}
      onClearFeatureSelection={onClearFeatureSelection}
    />
  );
}
